import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ParticipantSession } from "generated/participantsession/participantsession_pb";
import { StudyParticipant } from "generated/studyparticipant/studyparticipant_pb";

// Enum used for a11y support; to track the button that triggered the reload.
export enum FocusOn {
  None,
  Title,
  Exit,
  // StudyParticipants
  AddStudyParticipant,
  ChangeLogs,
  SwapWatches,
  // ParticipantDetails-specific buttons
  ParticipantCompliance,
  EditParticipantId,
  UpdateParticipantStatus,
  EditEnrollmentDate,
  EditCompletionDate,
  EditWithdrawalDate,
  ManageWatch,
}

/**
 * Stores information of the study participants table view.
 */
export interface StudyParticipantsViewState {
  pageNumber: number;
  studyParticipantId: string;
  studyParticipantSerialized: number[];
  participantSessionSerialized: number[];
  successMessage: string;
  errorMessage: string;
  // Set what button to focus on
  focusTarget: FocusOn;
}

export const initialState: StudyParticipantsViewState = {
  pageNumber: 0,
  studyParticipantId: "",
  studyParticipantSerialized: [],
  participantSessionSerialized: [],
  successMessage: "",
  errorMessage: "",
  focusTarget: FocusOn.None,
};

const studyParticipantsViewStateSlice = createSlice({
  name: "studyParticipantsViewState",
  initialState,
  reducers: {
    resetParticipantsViewState: (state) => {
      state.pageNumber = initialState.pageNumber;
      state.studyParticipantId = initialState.studyParticipantId;
      state.studyParticipantSerialized =
        initialState.studyParticipantSerialized;
      state.participantSessionSerialized =
        initialState.participantSessionSerialized;
      state.successMessage = initialState.successMessage;
      state.errorMessage = initialState.errorMessage;
      state.focusTarget = initialState.focusTarget;
    },
    resetSnackbarMessages: (state) => {
      state.successMessage = initialState.successMessage;
      state.errorMessage = initialState.errorMessage;
    },
    resetFocusTarget: (state) => {
      state.focusTarget = FocusOn.None;
    },
    setParticipantsViewState: (
      state,
      action: PayloadAction<StudyParticipantsViewState>
    ) => {
      state.pageNumber = action.payload.pageNumber;
    },
    setStudyParticipantDetails: (
      state,
      action: PayloadAction<StudyParticipantsViewState>
    ) => {
      state.pageNumber = action.payload.pageNumber;
      state.studyParticipantId = action.payload.studyParticipantId;
    },
    setStudyParticipantSerialized: (state, action: PayloadAction<number[]>) => {
      state.studyParticipantSerialized = action.payload;
    },
    setParticipantSessionSerialized: (
      state,
      action: PayloadAction<number[]>
    ) => {
      state.participantSessionSerialized = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    setFocusTarget: (state, action: PayloadAction<FocusOn>) => {
      state.focusTarget = action.payload;
    },
  },
});

export const selectStudyParticipant = createSelector(
  (state: StudyParticipantsViewState) => state.studyParticipantSerialized,
  (serialized) =>
    StudyParticipant.deserializeBinary(Uint8Array.from(serialized))
);

export const selectParticipantSession = createSelector(
  (state: StudyParticipantsViewState) => state.participantSessionSerialized,
  (serialized) =>
    ParticipantSession.deserializeBinary(Uint8Array.from(serialized))
);

export const {
  resetParticipantsViewState,
  resetSnackbarMessages,
  resetFocusTarget: resetFocusButton,
  setParticipantsViewState,
  setStudyParticipantDetails,
  setStudyParticipantSerialized,
  setParticipantSessionSerialized,
  setSuccessMessage,
  setErrorMessage,
  setFocusTarget,
} = studyParticipantsViewStateSlice.actions;

export default studyParticipantsViewStateSlice.reducer;
